import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";

import { buildThreadRouteParams } from "../threadRoutes";

/** The ref shape `buildThreadRouteParams` accepts, brands intact. */
type CanonicalThreadRef = Parameters<typeof buildThreadRouteParams>[0];

/**
 * Sends a draft route somewhere else once it stops being a draft.
 *
 * Two exits, and they are not the same. A draft that has been promoted has a
 * real thread and must hand over to the thread route — staying would show a
 * draft surface for work that has started. A draft that is neither promoted nor
 * present has been deleted or never existed, and its only honest destination is
 * home; rendering nothing forever is how a dead draft id became a blank screen.
 *
 * `search: (previous) => previous` on the handover is deliberate: the panel a
 * user had open should survive being promoted, and dropping the search would
 * close it at the exact moment the thread becomes real.
 */
export function useDraftRouteRedirects(input: {
  readonly canonicalThreadRef: CanonicalThreadRef | null;
  readonly hasDraftSession: boolean;
}): void {
  const navigate = useNavigate();
  const { canonicalThreadRef, hasDraftSession } = input;

  // Keyed on the destination, not on the object carrying it. The route derives
  // a fresh ref object on every store update, so depending on the object
  // navigated once per update while a turn streamed. Each of those navigations
  // aborted any in-flight view transition and kept the router committing, which
  // is how a handover became an update-depth crash.
  const params = canonicalThreadRef ? buildThreadRouteParams(canonicalThreadRef) : null;
  const targetEnvironmentId = params?.environmentId ?? null;
  const targetThreadId = params?.threadId ?? null;

  useEffect(() => {
    if (targetEnvironmentId === null || targetThreadId === null) return;
    void navigate({
      to: "/$environmentId/$threadId",
      params: { environmentId: targetEnvironmentId, threadId: targetThreadId },
      replace: true,
      search: (previous) => previous,
    });
  }, [navigate, targetEnvironmentId, targetThreadId]);

  const hasCanonicalThread = canonicalThreadRef !== null;
  useEffect(() => {
    if (hasDraftSession || hasCanonicalThread) return;
    void navigate({ to: "/", replace: true });
  }, [hasCanonicalThread, hasDraftSession, navigate]);
}
